import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { FlyerType } from '@/config/constants';
import celebrationSchoolIcon from '@/assets/celebration.svg';
import celebrationCommunityIcon from '@/assets/celebration_community.svg';
import upcomingSchoolIcon from '@/assets/upcoming.svg';
import upcomingCommunityIcon from '@/assets/upcoming_community.svg';
import { BodyMedium, BodySmallMidEmphasis, TitleSmallEmphasized } from '@/design-system';
import colors from '@/theme/colors';
import { AccessibleDivButton, FadeInWrapper } from '@/components';
import styles from './InfoBanner.styles';

interface Props {
  isNewFlyers: boolean;
  type: string;
  unreadExpiringAmount: number;
  updateAt: string;
  onClick: () => void;
  dataTestId?: string;
}

const InfoBanner: React.FC<Props> = ({
  isNewFlyers,
  type,
  unreadExpiringAmount,
  updateAt,
  onClick,
  dataTestId,
}) => {
  const { track } = useAnalytics();

  const title = isNewFlyers ? 'New' : 'Upcoming';
  const description = isNewFlyers
    ? 'Explore new flyers in the last 2 weeks'
    : 'Explore the calendar for  upcoming events';
  const amountFlyersLabel = isNewFlyers ? 'New unread' : 'Expiring this week';
  const flyerUpdateLabel = isNewFlyers ? 'Last update' : 'Schedule next';

  const handleClick = () => {
    track({
      category: `explore_page:${isNewFlyers ? 'new' : 'upcoming'}:banners`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: description,
    });
    onClick();
  };

  const getIcon = () => {
    if (isNewFlyers) {
      if (type === FlyerType.school) {
        return celebrationSchoolIcon;
      }
      return celebrationCommunityIcon;
    }
    if (type === FlyerType.school) {
      return upcomingSchoolIcon;
    }
    return upcomingCommunityIcon;
  };
  const icon = getIcon();

  return (
    <FadeInWrapper className={styles.container}>
      <AccessibleDivButton
        className={styles.content}
        onClick={handleClick}
        data-testid={dataTestId}
        role="link"
      >
        <div className={styles.header}>
          <img src={icon} width="56px" height="56px" alt="" className={styles.icon} />
          <TitleSmallEmphasized>{title}</TitleSmallEmphasized>
          <BodyMedium color={colors.slate}>{description}</BodyMedium>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            <BodySmallMidEmphasis color={colors.slate} className={styles.label}>
              {amountFlyersLabel}
            </BodySmallMidEmphasis>
            <TitleSmallEmphasized color={isNewFlyers ? colors.jungle : colors.orange} nonTranslate>
              {unreadExpiringAmount}
            </TitleSmallEmphasized>
          </div>
          <div className={styles.footerInfo}>
            <BodySmallMidEmphasis color={colors.slate} className={styles.label}>
              {flyerUpdateLabel}
            </BodySmallMidEmphasis>
            <TitleSmallEmphasized>{updateAt}</TitleSmallEmphasized>
          </div>
        </div>
      </AccessibleDivButton>
    </FadeInWrapper>
  );
};

export default InfoBanner;
